/*
 * cart-drawer.js — «Προστέθηκε στο καλάθι σου» (phase 2 of the Skroutz-style cart, Stelios 08/10/2026).
 *
 * The element is still Dawn's <cart-drawer>, so everything that adds to the cart and then calls
 * renderContents() — product-form.js (the product page, quick add), the card editor, the companion card — opens
 * it exactly as before. What it shows is snippets/cart-drawer.liquid: the line just added, free shipping, the
 * companion first, up to 4 tiles with «+», «Δες το καλάθι σου» / «Συνέχισε τις αγορές».
 *
 *  · The header's cart icon is a plain link to the cart page again: Dawn bound it to open this drawer, and the
 *    cart — quantities, removal, BOX NOW, checkout — is one place now (the proposal, «Το καλάθι είναι ένα»).
 *  · The line shown is the one the add returned (its key); the Liquid can only guess the newest.
 *  · «+» on a tile adds in place: one POST that asks for this panel and the cart bubble, and only the free-
 *    shipping bar, the total and the bubble are taken from it. The tile turns ✓ and nothing else moves under
 *    the customer's finger — no `cart-drawer-items` any more, so Dawn's cartUpdate re-render has nothing to
 *    replace. The companion card's own add ends the same way (panelAdded, from live-preview-companion.js).
 *  · A suggestion with the customer's names opens its page with the names (handOver(): the tab's
 *    sessionStorage, read once by assets/live-preview.js — never the URL, the words are a personal name).
 *  · The layout's copy is an empty shell (see the snippet); open() fetches the panel first if it is ever asked
 *    to open one.
 *  · GA4 `mb_added`: shown / rec_add / rec_open / view_cart / continue, with the product just added, and the
 *    tile's handle, position and kind (lp / quick / open) — never anything the customer typed.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // tile → the names drawn on its cover (paintNames), for handOver(); kept here, not in the page
    this.names = new WeakMap();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // one delegated listener: the panel's insides are replaced on every render
    this.addEventListener('click', (evt) => this.onClick(evt));
    // back from the cart page (the browser's back/forward cache): not the panel of an add that is over, with
    // a cart the customer may have changed since
    window.addEventListener('pageshow', (evt) => {
      if (evt.persisted && this.classList.contains('active')) this.close();
    });
  }

  get en() {
    return String(document.documentElement.lang || '').slice(0, 2) === 'en';
  }

  isShell() {
    return !!this.querySelector('[data-added-shell]');
  }

  open(triggeredBy) {
    if (this.isShell()) {
      // never an empty panel on screen: render it first (nothing in the theme opens it this way today)
      if (!this.opening) {
        this.opening = this.refresh().then(() => {
          this.opening = null;
          if (!this.isShell()) this.open(triggeredBy);
        });
      }
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.track('shown');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    const sections = (parsedState && parsedState.sections) || {};
    this.swapInner(sections['cart-drawer']);
    this.swapBubble(sections);
    // a single add answers with the line; an `items` add with the lines
    const key =
      parsedState && (parsedState.key || (parsedState.items && parsedState.items[0] && parsedState.items[0].key));
    this.showLine(key);
    this.paintNames();
    if (this.querySelector('[data-added-line]')) this.classList.remove('is-empty');
    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ---- the panel's own parts ---- */

  swapInner(html) {
    const inner = document.getElementById('CartDrawer');
    if (!html || !inner) return false;
    const src = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
    if (!src) return false;
    inner.innerHTML = src.innerHTML;
    return true;
  }

  swapBubble(sections) {
    const html = sections && sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (!html || !bubble) return;
    const src = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (src) bubble.innerHTML = src.innerHTML;
  }

  refresh() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => {
        this.swapInner(html);
        this.showLine(null);
        this.paintNames();
        this.classList.toggle('is-empty', !this.querySelector('[data-added-line]'));
      })
      .catch(() => {});
  }

  showLine(key) {
    const lines = this.querySelectorAll('[data-added-line]');
    if (!lines.length) return;
    let shown = null;
    if (key) lines.forEach((line) => { if (line.getAttribute('data-added-line') === String(key)) shown = line; });
    if (!shown) shown = this.querySelector('[data-added-line]:not([hidden])') || lines[0];
    lines.forEach((line) => { line.hidden = line !== shown; });
  }

  // The suggestions that carry the customer's names (snippets/added-recs.liquid, `data-lp-tile`): the words of
  // the line on show, by the companion card's 'copy' rule — the first book's title out, the names kept — drawn
  // on the cover by live-preview-cart.js. No words (a plain line on show): the product's photo instead.
  paintNames() {
    const tiles = this.querySelectorAll('[data-lp-tile]');
    if (!tiles.length) return;
    const line = this.querySelector('[data-added-line]:not([hidden])');
    const lineOverlay = line && line.querySelector('.lp-cart-overlay[data-lp-cart]');
    let sourceText = '';
    try {
      const desc = lineOverlay ? JSON.parse(lineOverlay.getAttribute('data-lp-cart')) : null;
      sourceText = (desc && desc.layers && desc.layers[0] && desc.layers[0].content) || '';
    } catch (e) {
      sourceText = '';
    }
    const lineName = line && line.querySelector('.mba__name');
    const sourceTitle = lineName ? lineName.textContent.trim() : '';
    const rules = window.LPCompanion;
    tiles.forEach((overlay) => {
      let cfg = null;
      try {
        cfg = JSON.parse(overlay.getAttribute('data-lp-tile'));
      } catch (e) {
        cfg = null;
      }
      let text = '';
      if (cfg && sourceText && rules && typeof rules.deriveText === 'function') {
        try {
          text = rules.deriveText('copy', sourceText, [sourceTitle, cfg.title || ''], '');
        } catch (e) {
          text = '';
        }
      }
      const cover = overlay.parentElement;
      const img = cover && cover.querySelector('img');
      const tile = overlay.closest('[data-added-tile]');
      if (!cfg || !String(text).trim()) {
        // nothing to write: the product as it is
        const photo = img && img.getAttribute('data-photo');
        if (img && photo) img.src = photo;
        overlay.remove();
        const tag = tile && tile.querySelector('.mba-tile__tag--names');
        if (tag) tag.hidden = true;
        return;
      }
      if (tile) this.names.set(tile, text);
      overlay.setAttribute('data-lp-cart', JSON.stringify({ base: cfg.base, layers: [{ content: text, box: cfg.box, font: cfg.font }] }));
      if (window.LPCart && typeof window.LPCart.render === 'function') window.LPCart.render(overlay);
    });
  }

  // «+» on a suggestion with names: the companion card for that product, in place, under the tile's row
  // (sections/added-card.liquid). Its own add ends in panelAdded() like the companion's; «Όχι, ευχαριστώ»
  // takes it away again.
  async openCard(btn) {
    if (btn.disabled || btn.classList.contains('is-done')) return;
    const tile = btn.closest('[data-added-tile]');
    const handle = btn.getAttribute('data-added-card');
    if (!tile || !handle) return;
    const open = this.querySelector(`[data-added-card-slot="${handle}"]`);
    if (open) {
      open.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
    const error = this.querySelector('[data-added-error]');
    if (error) error.hidden = true;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=added-card`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.shopify-section') || doc.body;
      if (!section || !section.querySelector('[data-lp-companion]')) throw new Error('no card');
      const slot = document.createElement('li');
      slot.className = 'mba-tiles__card';
      slot.setAttribute('data-added-card-slot', handle);
      slot.innerHTML = section.innerHTML;
      // after the tile's row, so nothing above it moves
      const tiles = [...tile.parentElement.querySelectorAll(':scope > [data-added-tile]')];
      const index = tiles.indexOf(tile);
      const rowEnd = tiles[Math.min(tiles.length - 1, index - (index % 2) + 1)] || tile;
      let anchor = rowEnd;
      while (anchor.nextElementSibling && anchor.nextElementSibling.hasAttribute('data-added-card-slot')) anchor = anchor.nextElementSibling;
      anchor.after(slot);
      btn.removeAttribute('aria-busy');
      btn.setAttribute('aria-expanded', 'true');
      slot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.track('rec_card', tile);
    } catch (e) {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      // no card to show: the product's own page has the full preview — with the names on it
      const link = tile.querySelector('a[href]');
      if (link) {
        this.handOver(tile);
        window.location.href = link.href;
      }
    }
  }

  // A suggestion that showed the customer's names, opened on its own page (its photo or its name — Stelios,
  // 08/10/2026: «ο πελάτης θα πατήσει πρώτα στο προϊόν… και εκεί δεν είναι γραμμένα τα ονόματα»). The page
  // must show the cover the tile showed, so the names go with it: once, in this tab's sessionStorage, where
  // assets/live-preview.js takes them on that product's page (takeHandoff) — never in the URL, which analytics
  // record and people share. The colour travels in the link (`?variant=`, snippets/added-recs.liquid).
  handOver(tile) {
    const text = tile && this.names.get(tile);
    if (!text) return;
    try {
      window.sessionStorage.setItem(
        'mb-lp-handoff',
        JSON.stringify({ p: tile.getAttribute('data-handle') || '', t: text, at: Date.now() })
      );
    } catch (e) {
      /* no storage (a private window, blocked): the page opens as it always did */
    }
  }

  // After an add from inside the panel (a tile's «+», the companion card): the bar, the total and the bubble
  // from the response's own render of this panel — and nothing else.
  panelAdded(response, card) {
    const sections = (response && response.sections) || {};
    const html = sections['cart-drawer'];
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      ['[data-added-ship]', '[data-added-sum]'].forEach((selector) => {
        const src = doc.querySelector(selector);
        const dst = this.querySelector(selector);
        if (src && dst) dst.innerHTML = src.innerHTML;
      });
    }
    this.swapBubble(sections);
    if (card) {
      card.classList.add('is-added');
      // a suggestion's own card: its tile turns ✓ too
      const slot = card.closest('[data-added-card-slot]');
      const btn = slot && this.querySelector(`[data-added-card="${slot.getAttribute('data-added-card-slot')}"]`);
      if (btn) this.markDone(btn);
    }
    this.updateFreeTags();
  }

  markDone(btn) {
    btn.disabled = true;
    btn.removeAttribute('aria-busy');
    btn.classList.add('is-done');
    btn.innerHTML = '<span aria-hidden="true">✓</span>';
    const tile = btn.closest('[data-added-tile]');
    const name = tile && tile.querySelector('.mba-tile__nm');
    btn.setAttribute('aria-label', (this.en ? 'In your cart: ' : 'Στο καλάθι: ') + (name ? name.textContent.trim() : ''));
    if (tile) tile.classList.add('is-added');
  }

  // «+ δωρεάν μεταφορικά» on the tiles that would now close the gap — none once it is closed, none on a tile
  // already in the cart
  updateFreeTags() {
    const bar = this.querySelector('[data-added-ship] [data-gap]');
    const gap = bar ? parseInt(bar.getAttribute('data-gap'), 10) || 0 : 0;
    this.querySelectorAll('[data-added-free]').forEach((tag) => {
      const price = parseInt(tag.getAttribute('data-price'), 10) || 0;
      const added = tag.closest('.is-added');
      tag.hidden = !!added || !(gap > 0 && price >= gap);
    });
  }

  onClick(evt) {
    const target = evt.target;
    if (!target || !target.closest) return;
    if (target.id === 'CartDrawer-Overlay') {
      this.close();
      return;
    }
    const add = target.closest('[data-added-add]');
    if (add) {
      evt.preventDefault();
      this.quickAdd(add);
      return;
    }
    const cardBtn = target.closest('[data-added-card]');
    if (cardBtn) {
      evt.preventDefault();
      this.openCard(cardBtn);
      return;
    }
    // «Όχι, ευχαριστώ» on a suggestion's own card: the card goes (live-preview-companion.js — its own listener
    // has already taken it out of the page, hence the event's path rather than closest()), its tile's «+» is
    // offered again
    const path = typeof evt.composedPath === 'function' ? evt.composedPath() : [];
    const has = (attr) => path.find((node) => node && node.hasAttribute && node.hasAttribute(attr));
    const slot = has('data-added-card-slot');
    if (slot && has('data-lp-companion-skip')) {
      const btn = this.querySelector(`[data-added-card="${slot.getAttribute('data-added-card-slot')}"]`);
      if (btn && !btn.classList.contains('is-done')) {
        btn.disabled = false;
        btn.setAttribute('aria-expanded', 'false');
      }
      setTimeout(() => { if (!slot.querySelector('[data-lp-companion]')) slot.remove(); });
      return;
    }
    if (target.closest('[data-added-continue]')) {
      this.track('continue');
      this.close();
      return;
    }
    if (target.closest('[data-added-cart]')) {
      this.track('view_cart');
      return;
    }
    const opener = target.closest('[data-added-open]');
    if (opener) {
      const tile = opener.closest('[data-added-tile]');
      this.handOver(tile);
      this.track('rec_open', tile);
    }
  }

  async quickAdd(btn) {
    if (btn.disabled || btn.classList.contains('is-done')) return;
    const tile = btn.closest('[data-added-tile]');
    const error = this.querySelector('[data-added-error]');
    if (error) error.hidden = true;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const addUrl = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(btn.getAttribute('data-variant')), quantity: 1 }],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      let json = null;
      try {
        json = await response.json();
      } catch (e) {
        json = null;
      }
      if (!response.ok || !json || json.status) throw new Error((json && (json.description || json.message)) || `HTTP ${response.status}`);
      this.markDone(btn);
      this.panelAdded(json);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'added-panel', productVariantId: btn.getAttribute('data-variant'), cartData: json });
      }
      this.track('rec_add', tile);
    } catch (e) {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      if (error) {
        error.textContent = this.en ? 'It did not go into the cart — please try again.' : 'Δεν μπήκε στο καλάθι — δοκίμασε ξανά.';
        error.hidden = false;
      }
    }
  }

  track(action, tile) {
    try {
      if (typeof window.gtag !== 'function') return;
      const panel = this.querySelector('[data-added-src]');
      const params = { action, src: panel ? panel.getAttribute('data-added-src') : '' };
      if (tile) {
        params.rec = tile.getAttribute('data-handle') || '';
        params.pos = Number(tile.getAttribute('data-pos')) || 0;
        params.kind = tile.getAttribute('data-kind') || '';
      }
      window.gtag('event', 'mb_added', params);
    } catch (e) {
      /* measuring never blocks the panel */
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

// Dawn's drawer items. Nothing renders <cart-drawer-items> any more (the panel shows one line, the cart page
// the cart); kept defined so that nothing that still looks the element up breaks.
if (typeof CartItems !== 'undefined' && !customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
